import { makeAutoObservable } from 'mobx'

/** Notification entity */
export interface Notification {
    id: number
    uuid: string

    user?: unknown
    createdBy?: unknown
    updatedBy?: unknown

    type: string
    title: string
    message: string

    entityCode?: string
    entityUuid?: string

    isReaded: boolean
    readedAt?: string | Date | null

    createdAt: string | Date
    updatedAt?: string | Date | null

    [key: string]: unknown
}

interface Pagination {
    page?: number
    limit?: number
    total?: number
}

/**
 * Optional scope that restricts the drawer to a single entity's notifications
 * (e.g. a driver assignment). When set, list/count loads add entityCode +
 * entityUuid to the server filters. Cleared when the drawer closes so the
 * global entry point (header bell) shows everything again.
 */
export interface NotificationEntityFilter {
    entityCode: string
    entityUuid: string
}

class NotificationManager {
    // ---------- UI ----------
    isDrawerOpen = false
    isFilterOpen = false
    isNotificationLoading = false
    isLoadingFirstPage = false

    // When set, the drawer is scoped to a single entity (e.g. an assignment).
    entityFilter: NotificationEntityFilter | null = null

    // ---------- DATA ----------
    notifications: Notification[] = []

    page = 1
    limit = 30
    total = 0
    unreadCount = 0

    // Per-tab unread counters for the notifications page tabs.
    // tasks — type IN (system, task); dispatcher — type = dispatcher.
    unreadTasksCount = 0
    unreadDispatcherCount = 0

    constructor() {
        makeAutoObservable(this)
    }

    get hasMore(): boolean {
        return this.notifications.length < this.total
    }

    // ---------- UI setters ----------
    setIsLoadingFirstPage(v: boolean): void {
        this.isLoadingFirstPage = v
    }

    setIsDrawerOpen(v: boolean): void {
        this.isDrawerOpen = v
        // Reset the entity scope when the drawer closes so the next open from
        // the global entry point (header bell) shows all notifications.
        if (!v) {
            this.entityFilter = null
        }
    }

    setEntityFilter(filter: NotificationEntityFilter | null): void {
        this.entityFilter = filter
    }

    // Open the drawer scoped to a single entity's notifications.
    openScoped(filter: NotificationEntityFilter): void {
        this.entityFilter = filter
        this.isDrawerOpen = true
    }

    setIsFilterOpen(v: boolean): void {
        this.isFilterOpen = v
    }

    setIsNotificationLoading(v: boolean): void {
        this.isNotificationLoading = v
    }

    // ---------- DATA setters ----------
    setNotifications(items: Notification[]): void {
        if (Array.isArray(items)) {
            this.notifications = items
        }
    }

    appendNotifications(items: Notification[]): void {
        if (Array.isArray(items)) {
            this.notifications = [...this.notifications, ...items]
        }
    }

    setPagination({ page, limit, total }: Pagination): void {
        if (page !== undefined) this.page = page
        if (limit !== undefined) this.limit = limit
        if (total !== undefined) this.total = total
    }

    markNotificationAsRead(uuid: string): void {
        this.notifications = this.notifications.map((item) => (item.uuid === uuid ? { ...item, isReaded: true } : item))
    }

    markAllAsRead(): void {
        this.notifications = this.notifications.map((item) =>
            item.isReaded ? item : { ...item, isReaded: true }
        )
    }

    setUnreadCount(count: number): void {
        this.unreadCount = count
    }

    setUnreadTasksCount(count: number): void {
        this.unreadTasksCount = count
    }

    setUnreadDispatcherCount(count: number): void {
        this.unreadDispatcherCount = count
    }

    incrementUnreadCount(): void {
        this.unreadCount += 1
    }

    decrementUnreadCount(): void {
        if (this.unreadCount > 0) this.unreadCount -= 1
    }

    clearUnreadCount(): void {
        this.unreadCount = 0
    }
}

export const notificationManager = new NotificationManager()
